import { useNavigate } from 'react-router-dom';
import { ArrowRight, Package, ShoppingCart, BarChart3, ShieldCheck, Zap, Activity, Clock, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import auth from '../utils/auth';
import { useState, useEffect } from 'react';

export default function LandingPage() {
  const navigate = useNavigate();
  const isAuthenticated = auth.isAuthenticated();
  const [systemStatus, setSystemStatus] = useState('OPERATIONAL');
  const [metrics, setMetrics] = useState({ uptime: 99.9, transactions: 0, active: 847 });

  useEffect(() => {
    // Simulate live metrics
    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        transactions: Math.floor(Math.random() * 9999) + 10000,
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleStart = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-body overflow-x-hidden cursor-custom">
      {/* Navigation */}
      <nav className="flex items-center justify-between p-4 md:px-12 border-b-4 border-slate-900 sticky top-0 bg-white z-50 backdrop-blur-sm bg-opacity-95">
        <div className="flex items-center gap-3 md:gap-4">
          <h1 className="text-3xl font-black font-heading tracking-tighter uppercase">
            Retail<span className="text-primary italic">Flow</span>
          </h1>
          <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-green-500 text-white text-xs font-black uppercase tracking-wider">
            <Activity size={12} className="animate-pulse" />
            {systemStatus}
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-4 flex-wrap justify-end">
          <div className="hidden md:block text-xs font-mono text-slate-500 tracking-tight shrink-0">
            v2.1.0 | EST. 2024
          </div>
          {isAuthenticated ? (
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="font-bold uppercase tracking-wide hover:bg-slate-900 hover:text-white transition-all text-sm md:text-base px-3 md:px-4 py-2">
              Dashboard
            </Button>
          ) : (
            <>
              <Button onClick={() => navigate('/login')} variant="ghost" className="font-bold uppercase tracking-wide hover:bg-slate-100 transition-all text-sm md:text-base px-3 md:px-4 py-2">
                Login
              </Button>
              <Button onClick={() => navigate('/register')} className="font-bold uppercase tracking-wide relative overflow-hidden group text-sm md:text-base px-3 md:px-4 py-2 ">
                <span className="relative z-10">Get Started</span>
                <div className="absolute inset-0 bg-accent transform translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
              </Button>
            </>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 px-6 md:px-12 border-b-4 border-slate-900 flex flex-col md:flex-row items-center gap-12 overflow-hidden">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.5) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(15, 23, 42, 0.5) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }} />
        
        <div className="flex-1 space-y-8 z-10">
          <div className="inline-block bg-accent py-1 px-4 text-white font-black uppercase tracking-widest text-sm mb-4 relative group">
            Next-Gen Inventory POS
            <div className="absolute inset-0 border-2 border-accent transform translate-x-1 translate-y-1 -z-10 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform" />
          </div>
          <h2 className="text-6xl md:text-8xl font-black font-heading tracking-tighter leading-none uppercase">
            Precision <br />
            <span className="text-primary inline-block hover:skew-y-2 transition-transform">Inventory</span> <br />
            Control.
          </h2>
          <p className="text-xl md:text-2xl text-slate-600 max-w-xl font-medium leading-relaxed">
            A high-density, Swiss-engineered management system for retail environments that demand absolute accuracy and speed.
          </p>
          
          {/* Live Metrics */}
          <div className="flex flex-wrap gap-6 pt-4 font-mono text-sm">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 border-2 border-slate-900">
              <TrendingUp size={16} className="text-green-600" />
              <span className="font-black">{metrics.uptime}%</span>
              <span className="text-slate-500">UPTIME</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 border-2 border-slate-900">
              <Clock size={16} className="text-blue-600" />
              <span className="font-black">{metrics.transactions.toLocaleString()}</span>
              <span className="text-slate-500">TX/DAY</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 border-2 border-slate-900">
              <Activity size={16} className="text-accent animate-pulse" />
              <span className="font-black">{metrics.active}</span>
              <span className="text-slate-500">ACTIVE</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 pt-4">
            <Button onClick={handleStart} size="lg" className="h-16 px-8 text-xl font-black uppercase tracking-wide group relative overflow-hidden">
              <span className="relative z-10 flex items-center">
                {isAuthenticated ? 'Go to Dashboard' : 'Initialize System'}
                <ArrowRight className="ml-4 transition-transform group-hover:translate-x-2" size={24} />
              </span>
              <div className="absolute inset-0 bg-accent transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
            </Button>
            <Button variant="outline" size="lg" className="h-16 px-8 text-xl font-black uppercase tracking-wide border-4 hover:bg-slate-900 hover:text-white transition-all duration-300">
              Feature Audit
            </Button>
          </div>
        </div>
        
        <div className="flex-1 relative group">
          <div className="relative z-10 border-8 border-slate-900 shadow-[20px_20px_0px_0px_rgba(0,47,167,1)] bg-white p-2 transition-all duration-500 group-hover:shadow-[25px_25px_0px_0px_rgba(0,47,167,1)] group-hover:rotate-1">
             <img 
               src="https://images.unsplash.com/photo-1758670496782-cb11b7b56675?q=80&w=1200" 
               alt="System Interface" 
               className="grayscale hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
             />
             {/* Scan line effect */}
             <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/20 to-transparent h-32 animate-scan pointer-events-none" />
          </div>
          {/* Abstract Swiss elements */}
          <div className="absolute -top-12 -right-12 w-64 h-64 bg-accent opacity-10 rounded-full blur-3xl -z-1 group-hover:opacity-20 transition-opacity" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-primary opacity-20 rounded-none -z-1 group-hover:rotate-45 transition-transform duration-700" />
        </div>
      </section>

      {/* Features Grid */}
      <section className="grid grid-cols-1 md:grid-cols-3 border-b-4 border-slate-900">
        <div className="p-12 border-b-4 md:border-b-0 md:border-r-4 border-slate-900 hover:bg-slate-50 transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-4 right-4 font-mono text-xs text-slate-300 font-black">01</div>
          <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center mb-8 rounded-none transform group-hover:rotate-12 transition-transform duration-300">
            <Package size={32} />
          </div>
          <h3 className="text-3xl font-black font-heading uppercase mb-4 tracking-tighter">Real-Time Stock</h3>
          <p className="text-slate-600 font-medium leading-relaxed">
            Absolute synchronization across all sales points. Automated low-stock alerts with precision thresholding.
          </p>
          <div className="mt-6 font-mono text-xs text-slate-400 tracking-tight">
            → LATENCY: &lt;50ms | ACCURACY: 99.97%
          </div>
        </div>
        
        <div className="p-12 border-b-4 md:border-b-0 md:border-r-4 border-slate-900 bg-primary text-white hover:bg-primary/90 transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-4 right-4 font-mono text-xs text-blue-200 font-black">02</div>
          <div className="w-16 h-16 bg-white text-primary flex items-center justify-center mb-8 rounded-none transform group-hover:-rotate-12 transition-transform duration-300">
            <ShoppingCart size={32} />
          </div>
          <h3 className="text-3xl font-black font-heading uppercase mb-4 tracking-tighter">Performance POS</h3>
          <p className="text-blue-100 font-medium leading-relaxed">
            Engineered for high-volume transactions. A 'Tetris' grid system that eliminates scanning friction.
          </p>
          <div className="mt-6 font-mono text-xs text-blue-200 tracking-tight">
            → SPEED: 2.3s/TX | THROUGHPUT: 1200/hr
          </div>
        </div>

        <div className="p-12 hover:bg-slate-50 transition-all duration-300 group relative overflow-hidden">
          <div className="absolute top-4 right-4 font-mono text-xs text-slate-300 font-black">03</div>
          <div className="w-16 h-16 bg-slate-900 text-white flex items-center justify-center mb-8 rounded-none transform group-hover:rotate-12 transition-transform duration-300">
            <BarChart3 size={32} />
          </div>
          <h3 className="text-3xl font-black font-heading uppercase mb-4 tracking-tighter">Data Analytics</h3>
          <p className="text-slate-600 font-medium leading-relaxed">
            High-density reporting with Swiss-standard legibility. Export critical business data in one click.
          </p>
          <div className="mt-6 font-mono text-xs text-slate-400 tracking-tight">
            → FORMATS: CSV, JSON, XML | API v2.1
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 md:px-12 border-b-4 border-slate-900 bg-slate-50">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { value: '500K+', label: 'TRANSACTIONS/MO', icon: TrendingUp },
            { value: '99.9%', label: 'SYSTEM UPTIME', icon: Activity },
            { value: '<50ms', label: 'AVG LATENCY', icon: Zap },
            { value: '24/7', label: 'SUPPORT ACTIVE', icon: ShieldCheck }
          ].map((stat, i) => (
            <div key={i} className="text-center group cursor-pointer">
              <stat.icon className="mx-auto mb-4 text-primary group-hover:scale-110 transition-transform" size={32} />
              <div className="text-4xl md:text-5xl font-black font-heading tracking-tighter mb-2">{stat.value}</div>
              <div className="text-xs font-mono text-slate-500 tracking-widest uppercase">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-12 px-6 border-b-4 border-slate-900 bg-white overflow-hidden whitespace-nowrap">
        <div className="flex items-center animate-scroll gap-20">
          {[1,2,3,4,5,6,7,8].map(i => (
            <div key={i} className="flex items-center gap-4 text-slate-400 font-black uppercase tracking-widest text-2xl">
              <Zap size={24} className="text-accent" />
              Swiss Precision
              <ShieldCheck size={24} className="text-primary" />
              Enterprise Grade
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial Bar */}
      <section className="py-12 px-6 md:px-12 border-b-4 border-slate-900 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { quote: 'Reduced inventory errors by 94% in first quarter', company: 'TECHMART SYSTEMS' },
            { quote: 'Transaction speed improved 3x versus legacy POS', company: 'URBAN RETAIL CO' },
            { quote: 'ROI achieved within 8 weeks of deployment', company: 'PRECISION STORES' }
          ].map((testimonial, i) => (
            <div key={i} className="p-6 border-2 border-slate-700 hover:border-primary transition-colors group">
              <div className="text-xs font-mono text-slate-500 mb-3 tracking-widest">CASE_STUDY_{String(i + 1).padStart(2, '0')}</div>
              <p className="text-lg font-medium mb-4 leading-relaxed group-hover:text-primary transition-colors">"{testimonial.quote}"</p>
              <div className="text-xs font-black text-slate-400 tracking-widest">— {testimonial.company}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 px-6 md:px-12 text-center bg-white relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #0f172a, #0f172a 2px, transparent 2px, transparent 4px),
                           repeating-linear-gradient(90deg, #0f172a, #0f172a 2px, transparent 2px, transparent 4px)`,
          backgroundSize: '100px 100px'
        }} />
        
        <div className="max-w-4xl mx-auto space-y-12 relative z-10">
          <div className="inline-block px-4 py-1 bg-slate-900 text-white text-xs font-mono tracking-widest mb-4">
            SYSTEM_INITIALIZATION_READY
          </div>
          <h2 className="text-5xl md:text-7xl font-black font-heading uppercase tracking-tighter leading-none">
            Ready to <span className="text-accent inline-block hover:scale-110 transition-transform">Standardize</span> Your Operations?
          </h2>
          <p className="text-xl md:text-2xl text-slate-600 font-medium max-w-2xl mx-auto">
            Join the systems of record used by high-performance retail labs worldwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button onClick={handleStart} size="lg" className="h-20 px-12 text-2xl font-black uppercase tracking-tighter rounded-none relative overflow-hidden group">
              <span className="relative z-10">Initialize Now</span>
              <div className="absolute inset-0 bg-accent transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
            </Button>
            <Button onClick={() => navigate('/register')} variant="outline" size="lg" className="h-20 px-12 text-2xl font-black uppercase tracking-tighter border-4 border-slate-900 rounded-none hover:bg-slate-900 hover:text-white transition-all duration-300">
              Request Demo
            </Button>
          </div>
          <div className="pt-8 flex items-center justify-center gap-8 text-xs font-mono text-slate-400">
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              NO CREDIT CARD
            </span>
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              14-DAY TRIAL
            </span>
            <span className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              INSTANT SETUP
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white p-12 md:p-24 border-t-8 border-primary">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2 space-y-6">
            <h2 className="text-4xl font-black font-heading tracking-tighter uppercase">
              Retail<span className="text-primary">Flow</span>
            </h2>
            <p className="max-w-md text-slate-400 font-medium leading-relaxed">
              Utilitarian inventory management for the modern retail environment. 
              Designed for speed, built for precision, engineered for scale.
            </p>
            <div className="flex items-center gap-3 pt-4">
              <div className="px-3 py-1 bg-green-500/20 border border-green-500 text-green-400 text-xs font-mono tracking-wider">
                ALL SYSTEMS NOMINAL
              </div>
              <div className="px-3 py-1 bg-slate-800 text-slate-400 text-xs font-mono tracking-wider">
                v2.1.0
              </div>
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="font-black uppercase tracking-widest text-sm text-primary">System</h4>
            <ul className="space-y-2 text-slate-400 font-medium text-lg">
              <li className="hover:text-white cursor-pointer hover:translate-x-1 transition-all">→ Protocol</li>
              <li className="hover:text-white cursor-pointer hover:translate-x-1 transition-all">→ Documentation</li>
              <li className="hover:text-white cursor-pointer hover:translate-x-1 transition-all">→ API Keys</li>
              <li className="hover:text-white cursor-pointer hover:translate-x-1 transition-all">→ Service Status</li>
            </ul>
          </div>
          <div className="space-y-4">
            <h4 className="font-black uppercase tracking-widest text-sm text-primary">Compliance</h4>
            <ul className="space-y-2 text-slate-400 font-medium text-lg">
              <li className="hover:text-white cursor-pointer hover:translate-x-1 transition-all">→ GDPR-S</li>
              <li className="hover:text-white cursor-pointer hover:translate-x-1 transition-all">→ Terms of Ops</li>
              <li className="hover:text-white cursor-pointer hover:translate-x-1 transition-all">→ Legal Audit</li>
              <li className="hover:text-white cursor-pointer hover:translate-x-1 transition-all">→ Privacy</li>
            </ul>
          </div>
        </div>
        <div className="mt-20 pt-12 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">
            © 2026 RETAILFLOW SYSTEMS INTERNATIONAL. ALL RIGHTS RESERVED.
          </p>
          <div className="flex gap-8">
            <div className="w-10 h-1 bg-primary hover:h-2 transition-all cursor-pointer" />
            <div className="w-10 h-1 bg-accent hover:h-2 transition-all cursor-pointer" />
            <div className="w-10 h-1 bg-white hover:h-2 transition-all cursor-pointer" />
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          display: flex;
          width: 200%;
          animation: scroll 30s linear infinite;
        }
        @keyframes scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(500%); }
        }
        .animate-scan {
          animation: scan 3s linear infinite;
        }
        .cursor-custom {
          cursor: default;
        }
        .cursor-custom button:hover,
        .cursor-custom a:hover {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}